import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/authService';
import { colors } from '../styles/colors';
import RecruiterCompanyForm from './RecruiterCompanyForm';
import ConfirmModal from './ConfirmModal';
import Pagination from './Pagination';
import { useDebounce } from '../hooks/useDebounce';

interface RecruiterCompany {
  id: number;
  company_id: number;
  company_name: string;
  company_status: number;
  owner: boolean;
  active: boolean;
  pending: boolean;
  publish_opportunity: boolean;
  candidates_database_access: boolean;
}

interface Props {
  recruiterId: number;
  recruiterName: string;
  onClose: () => void;
}

const RecruiterCompaniesModal: React.FC<Props> = ({ recruiterId, recruiterName, onClose }) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<RecruiterCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 5;
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<RecruiterCompany | null>(null);
  const [editingCompany, setEditingCompany] = useState<RecruiterCompany | null>(null);

  useEffect(() => {
    fetchCompanies();
  }, [recruiterId, currentPage]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/recruiter-list/${recruiterId}/companies?page=${currentPage}&limit=${itemsPerPage}`);
      setCompanies(response.data.companies);
      setTotalPages(response.data.pagination.totalPages);
      setTotal(response.data.pagination.total);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleCompanyClick = (companyId: number) => {
    navigate(`/companies?companyId=${companyId}`);
    onClose();
  };

  const toggleActionMenu = (companyId: number) => {
    setOpenActionMenu(openActionMenu === companyId ? null : companyId);
  };

  const handleEdit = (company: RecruiterCompany) => {
    setEditingCompany(company);
    setShowForm(true);
    setOpenActionMenu(null);
  };

  const handleDelete = (company: RecruiterCompany) => {
    setCompanyToDelete(company);
    setShowConfirmModal(true);
    setOpenActionMenu(null);
  };

  const confirmDelete = async () => {
    if (companyToDelete) {
      try {
        await api.delete(`/recruiter-list/${recruiterId}/companies/${companyToDelete.id}`);
        toast.success('Empresa removida com sucesso!');
        fetchCompanies();
      } catch (error) {
        toast.error('Erro ao remover empresa');
      }
    }
    setShowConfirmModal(false);
    setCompanyToDelete(null);
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setCompanyToDelete(null);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setShowAddModal(false);
    setEditingCompany(null);
    fetchCompanies();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setShowAddModal(false);
    setEditingCompany(null);
  };

  return (
    <div style={styles.overlay} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Empresas - {recruiterName}</h2>
          <div style={styles.headerActions}>
            <button onClick={() => setShowAddModal(true)} style={styles.addBtn}>
              Adicionar Empresa
            </button>
            <button onClick={onClose} style={styles.closeBtn}>×</button>
          </div>
        </div>

        {loading ? (
          <div style={styles.loading}>Carregando...</div>
        ) : companies.length === 0 ? (
          <div style={styles.empty}>Nenhuma empresa encontrada</div>
        ) : (
          <div style={styles.content}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.thCenter}>Status da Empresa</th>
                  <th style={styles.thCenter}>Owner</th>
                  <th style={styles.thCenter}>Permissões</th>
                  <th style={styles.thCenter}>Active</th>
                  <th style={styles.thCenter}>Pending</th>
                  <th style={styles.thCenter}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id} style={styles.tr}>
                    <td style={styles.td}>{company.company_id}</td>
                    <td style={styles.td}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleCompanyClick(company.company_id);
                        }}
                        style={styles.companyLink}
                      >
                        {company.company_name}
                      </a>
                    </td>
                    <td style={styles.tdCenter}>
                      {company.company_status === 1 ? (
                        <span style={styles.yesBadge}>ATIVO</span>
                      ) : (
                        <span style={styles.noBadge}>INATIVO</span>
                      )}
                    </td>
                    <td style={styles.tdCenter}>
                      <span style={company.owner ? styles.yesBadge : styles.noBadge}>
                        {company.owner ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.tdCenter}>
                      <div style={styles.permissions}>
                        {company.publish_opportunity ? <span style={styles.permBadge}>Publicar</span> : null}
                        {company.candidates_database_access ? <span style={styles.permBadge}>BD</span> : null}
                        {!company.publish_opportunity && !company.candidates_database_access ? '-' : null}
                      </div>
                    </td>
                    <td style={styles.tdCenter}>
                      <span style={company.active ? styles.yesBadge : styles.noBadge}>
                        {company.active ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.tdCenter}>
                      <span style={company.pending ? styles.yesBadge : styles.noBadge}>
                        {company.pending ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(company.id);
                          }}
                          style={styles.actionMenuBtn}
                        >
                          ⋮
                        </button>
                        {openActionMenu === company.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => handleEdit(company)}
                              style={styles.actionMenuItem}
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleDelete(company)}
                              style={{...styles.actionMenuItem, color: colors.error}}
                            >
                              Excluir
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {totalPages > 1 && (
              <div style={styles.paginationContainer}>
                <div style={styles.paginationInfo}>
                  Página {currentPage} de {totalPages} ({total} total)
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      {showForm && editingCompany && (
        <RecruiterCompanyForm
          recruiterId={recruiterId}
          company={editingCompany}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
      
      {showAddModal && (
        <RecruiterCompanyForm
          recruiterId={recruiterId}
          company={null}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
      
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover a empresa ${companyToDelete?.company_name}?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Remover"
        cancelText="Cancelar"
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1400px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: colors.text,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: colors.textLight,
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: colors.textLight,
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: colors.textLight,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: `2px solid ${colors.border}`,
    color: colors.text,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  thCenter: {
    textAlign: 'center',
    padding: '12px',
    borderBottom: `2px solid ${colors.border}`,
    color: colors.text,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '12px',
    color: colors.text,
    textAlign: 'left',
  },
  tdCenter: {
    padding: '12px',
    color: colors.text,
    textAlign: 'center',
  },
  yesBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  noBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  permissions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.25rem',
    flexWrap: 'wrap',
  },
  permBadge: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '0.15rem 0.4rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
  },
  tdActions: {
    padding: '12px',
    color: colors.text,
    textAlign: 'center',
    position: 'relative',
    minWidth: '80px',
  },
  actionMenuContainer: {
    position: 'relative',
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  searchForm: {
    display: 'flex',
    gap: '12px',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
  },
  searchInput: {
    flex: 1,
    padding: '12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  searchBtn: {
    padding: '12px 24px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  companyLink: {
    color: colors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  paginationContainer: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  paginationInfo: {
    textAlign: 'center',
    color: colors.textLight,
  },
};

export default RecruiterCompaniesModal;
